import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  sendEmailVerification,
  updateProfile
} from 'firebase/auth';
import { serverTimestamp } from 'firebase/firestore';
import { auth } from '../config/firebase';
import firebaseService from '../services/firebaseService';
import { verifyFirebaseSetup, logFirebaseStatus } from '../utils/firebaseVerification';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = React.memo(({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [authStep, setAuthStep] = useState('initial'); // 'initial', 'signup', 'signin', 'verified'
  const [authError, setAuthError] = useState('');
  // Set when the user signs up or signs in with Google from the login modal, which is only
  // possible after ticking the terms checkbox; used to stamp termsAcceptedAt on new profiles
  const termsAcceptedRef = useRef(false);

  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    // Verify Firebase setup first
    const checkFirebase = async () => {
      try {
        if (!isMounted) return;

        logFirebaseStatus();
        const firebaseStatus = await verifyFirebaseSetup();

        if (!firebaseStatus.firebase) {
          if (isMounted) setLoading(false);
          return;
        }

        // Listen to Firebase authentication state changes ONLY
        unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
          if (!isMounted) return;



          if (firebaseUser) {
            // Set basic user data immediately
            const userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              name: firebaseUser.displayName ||
                (firebaseUser.email ? firebaseUser.email.split('@')[0].charAt(0).toUpperCase() + firebaseUser.email.split('@')[0].slice(1) : 'User'),
              emailVerified: firebaseUser.emailVerified,
              loginMethod: firebaseUser.providerData[0]?.providerId === 'google.com' ? 'google' : 'email'
            };

            if (isMounted) {
              setUser(userData);
              setAuthStep('verified');
              setProfileLoading(true); // Start profile loading immediately
            }
          } else {
            if (isMounted) {
              setUser(null);
              setHasPurchased(false);
              setAuthStep('initial');
              setProfileLoading(false); // No profile loading needed
            }
          }

          if (isMounted) setLoading(false);
        });


      } catch (error) {
        if (isMounted) setLoading(false);
      }
    };

    checkFirebase();

    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []); // Empty dependency array to run only once

  // Separate effect for Firestore operations
  useEffect(() => {
    let isMounted = true;

    const loadUserProfile = async () => {
      if (!user?.uid) return;

      try {
        setProfileLoading(true);
        // Get user profile from Firestore
        let userProfile = await firebaseService.getUserProfile(user.uid);

        // Only a confirmed-missing profile is treated as a new user. A failed read must never
        // lead to a write, or an existing hasPurchased: true record could be reset.
        if (userProfile.notFound && isMounted) {
          // Check if there is an existing completed order for this user
          const orderCheck = await firebaseService.checkUserHasPaidOrder(user.uid, user.email);
          const hasPurchasedFromOrder = orderCheck.hasPaidOrder;
          const accessTypeFromOrder = orderCheck.hasPaidOrder ? (orderCheck.accessType || 'web_access') : null;

          // Create user profile if it doesn't exist
          const profileData = {
            email: user.email,
            displayName: user.name,
            createdAt: serverTimestamp(),
            termsAcceptedAt: termsAcceptedRef.current ? serverTimestamp() : null,
            phoneVerified: false,
            hasPurchased: hasPurchasedFromOrder,
            accessType: accessTypeFromOrder,
            hasUpgraded: false,
            loginMethod: user.loginMethod
          };

          const createResult = await firebaseService.createUserProfile(user.uid, profileData);

          if (createResult.alreadyExists) {
            // Profile appeared since the read above - load it instead of treating the user as new
            userProfile = await firebaseService.getUserProfile(user.uid);
          } else if (isMounted) {
            const newUserData = {
              ...user,
              phoneVerified: false,
              hasPurchased: hasPurchasedFromOrder,
              accessType: accessTypeFromOrder,
              hasUpgraded: false,
              hasHardCopy: accessTypeFromOrder === 'hard_copy' || accessTypeFromOrder === 'both',
              createdAt: new Date().toISOString(),
              lastLogin: new Date().toISOString()
            };

            setUser(newUserData);
            setHasPurchased(hasPurchasedFromOrder);
            return;
          }
        }

        if (userProfile.success && isMounted) {
          let hasPurchasedStatus = userProfile.data.hasPurchased || false;
          let currentAccessType = userProfile.data.accessType || null;

          // Auto-reconciliation: If user profile says not purchased, check the orders collection
          if (!hasPurchasedStatus) {
            const orderCheck = await firebaseService.checkUserHasPaidOrder(user.uid, user.email);
            if (orderCheck.hasPaidOrder) {
              hasPurchasedStatus = true;
              currentAccessType = orderCheck.accessType || 'web_access';
              // Auto-heal the profile in Firestore
              firebaseService.updateBookAccess(user.uid, currentAccessType, true, orderCheck.order?.amount || null).catch(console.error);
            }
          }

          const updatedUserData = {
            ...user,
            phone: userProfile.data.phone,
            phoneVerified: userProfile.data.phoneVerified || false,
            hasPurchased: hasPurchasedStatus,
            accessType: currentAccessType,
            hasUpgraded: userProfile.data.hasUpgraded || false,
            hasHardCopy: currentAccessType === 'hard_copy' || currentAccessType === 'both',
            createdAt: userProfile.data.createdAt,
            lastLogin: new Date().toISOString(),
            loginMethod: userProfile.data.loginMethod || user.loginMethod
          };

          setUser(updatedUserData);
          setHasPurchased(hasPurchasedStatus);
        } else if (!userProfile.success && !userProfile.notFound && isMounted) {
          // Profile couldn't be loaded (network/permissions). Fall back to a read-only orders
          // check so paying users aren't locked out, without writing anything
          console.error('Could not load user profile:', userProfile.error);
          const orderCheck = await firebaseService.checkUserHasPaidOrder(user.uid, user.email);
          if (orderCheck.hasPaidOrder && isMounted) {
            const accessTypeFromOrder = orderCheck.accessType || 'web_access';
            setUser(prev => ({
              ...prev,
              hasPurchased: true,
              accessType: accessTypeFromOrder,
              hasHardCopy: accessTypeFromOrder === 'hard_copy' || accessTypeFromOrder === 'both'
            }));
            setHasPurchased(true);
          }
        }
      } catch (error) {
        // Error loading user profile
        console.error('Error loading user profile:', error);
      } finally {
        if (isMounted) {
          setProfileLoading(false);
        }
      }
    };

    loadUserProfile();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]); // Only run when user.uid changes

  // Google OAuth Sign In
  const signInWithGoogle = async () => {
    try {
      setAuthError('');
      const provider = new GoogleAuthProvider();

      // Request additional scopes to get user profile information
      provider.addScope('profile');
      provider.addScope('email');

      // Always show Google's account chooser so users can switch or add a different account
      provider.setCustomParameters({ prompt: 'select_account' });
      termsAcceptedRef.current = true;

      const result = await signInWithPopup(auth, provider);

      // User will be automatically signed in and onAuthStateChanged will handle the rest
      return { success: true, user: result.user };
    } catch (error) {
      const errorMessage = error.code === 'auth/popup-closed-by-user'
        ? 'Sign-in was cancelled'
        : 'Google sign-in failed. Please try again.';
      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Email/Password Sign Up
  const signUpWithEmail = async (email, password, displayName) => {
    try {
      setAuthError('');
      termsAcceptedRef.current = true;
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update profile with display name
      await updateProfile(result.user, { displayName });

      // Send email verification
      await sendEmailVerification(result.user);

      // User will be automatically signed in and onAuthStateChanged will handle the rest
      return { success: true, user: result.user };
    } catch (error) {
      let errorMessage = 'Sign-up failed. Please try again.';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters long.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      }

      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Email/Password Sign In
  const signInWithEmail = async (email, password) => {
    try {
      setAuthError('');
      const result = await signInWithEmailAndPassword(auth, email, password);

      // User will be automatically signed in and onAuthStateChanged will handle the rest
      return { success: true, user: result.user };
    } catch (error) {
      let errorMessage = 'Sign-in failed. Please try again.';

      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Please sign up instead.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }

      setAuthError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Legacy WhatsApp OTP methods (to be removed later)
  const login = async (userData, token) => {
    try {
      // For WhatsApp OTP flow, we'll create a custom user
      const customUserId = firebaseService.generateId();

      // Create user profile in Firestore
      const profileData = {
        displayName: userData.name,
        phone: userData.phone,
        phoneVerified: true,
        hasPurchased: false,
        accessType: null,
        loginMethod: 'whatsapp',
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      };

      await firebaseService.createUserProfile(customUserId, profileData);

      const userWithMetadata = {
        uid: customUserId,
        name: userData.name,
        phone: userData.phone,
        phoneVerified: true,
        hasPurchased: false,
        accessType: null,
        loginMethod: 'whatsapp',
        createdAt: new Date().toISOString(),
        lastLogin: new Date().toISOString()
      };

      setUser(userWithMetadata);
      setAuthStep('verified');

      return { success: true, user: userWithMetadata };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await firebaseService.signOutUser();
      setUser(null);
      setHasPurchased(false);
      setAuthStep('initial');
      setAuthError('');
    } catch (error) {
      // Handle logout error silently
    }
  };

  const setPurchaseStatus = async (status, accessType = 'web_access', paymentAmount = null) => {
    if (!user) return { success: false, error: 'No user logged in' };

    try {
      const accessResult = await firebaseService.updateBookAccess(user.uid, accessType, status, paymentAmount);
      setHasPurchased(status);

      // Update local user state
      const updatedUser = {
        ...user,
        hasPurchased: status,
        accessType: status ? accessType : null,
        hasUpgraded: status && accessType === 'both' ? true : user.hasUpgraded,
        hasHardCopy: status && (accessType === 'hard_copy' || accessType === 'both') ? true : false,
        purchaseDate: status ? new Date().toISOString() : null,
        paymentAmount: status ? paymentAmount : null
      };
      setUser(updatedUser);

      // Local state is updated regardless so a paying user isn't locked out this session,
      // but report the Firestore failure so callers can log it
      return accessResult.success ? { success: true } : { success: false, error: accessResult.error };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Legacy OTP methods (to be removed)
  const initiateOTP = (phoneNumber, name) => {
    // This will be removed when we fully migrate to new auth system
  };

  const verifyOTP = async (otp) => {
    // This will be removed when we fully migrate to new auth system
    return { success: false, error: 'OTP method deprecated. Use new authentication methods.' };
  };

  const resendOTP = () => {
    // This will be removed when we fully migrate to new auth system
    return { success: false, error: 'OTP method deprecated. Use new authentication methods.' };
  };

  const updateUserProfile = async (updates) => {
    if (!user) return { success: false, error: 'No user logged in' };

    try {
      await firebaseService.updateUserProfile(user.uid, updates);

      // Update local user state
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const isUserRegistered = () => {
    return user && (user.emailVerified || user.phoneVerified);
  };

  const canAccessBook = async () => {
    if (!user) return false;

    try {
      const accessCheck = await firebaseService.checkUserAccess(user.uid, user.email);
      return accessCheck.success && accessCheck.hasAccess;
    } catch (error) {
      return false;
    }
  };

  const value = {
    user,
    hasPurchased,
    authStep,
    authError,
    signInWithGoogle,
    signUpWithEmail,
    signInWithEmail,
    login, // Legacy - to be removed
    logout,
    setPurchaseStatus,
    initiateOTP, // Legacy - to be removed
    verifyOTP, // Legacy - to be removed
    resendOTP, // Legacy - to be removed
    updateUserProfile,
    isUserRegistered,
    canAccessBook,
    loading: loading || profileLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
});
